/**
 * A report's thread and its history — shared by «My reports» and the triage screen (bata-platform
 * ADR 1020, ADR 9071).
 *
 * **The thread** is the conversation between the delivery team and the report's author: each
 * message says who wrote it — «You» or «The team» to the author, the team member's name to the team
 * — and when. A question that asked for information is the history's «Information needed» move.
 * Every body is the writer's own text, drawn as text (ADR 9060/9063): nothing in it is parsed,
 * linked or run.
 *
 * **The history** is every move of the report: the status it went to and when. The team's also
 * says the relabel and who moved it — the side, and the person's name when the platform holds one;
 * the author's carries no actor, so it says the status and the date.
 */

import { useTranslation } from 'react-i18next';

import { formatDateTime } from '../../../lib/format';
import type { ReportMessage, ReportTransition } from '../../feedback/api';
import { ReportStatus } from '../../feedback/ReportStatus';
import { ACTOR_SIDE_LABEL_KEY, kindWords } from '../../feedback/words';

/** Whose view: the author never sees a team member's name. */
export type ThreadViewer = 'author' | 'team';

function useAt(): (iso: string) => string {
  const { t, i18n } = useTranslation();
  return (iso) => t('feedback.reports.at', { time: formatDateTime(iso, i18n.language, 'UTC') });
}

export function ReportThread({ messages, viewer }: {
  readonly messages: readonly ReportMessage[];
  readonly viewer: ThreadViewer;
}): React.JSX.Element {
  const { t } = useTranslation();
  const at = useAt();
  if (messages.length === 0) {
    return <p className="text-body text-ink-secondary" data-thread-empty="">{t('feedback.thread.empty')}</p>;
  }
  const writer = (message: ReportMessage): string => {
    if (message.side === 'author') return t(viewer === 'author' ? 'feedback.thread.you' : 'feedback.thread.author');
    if (viewer === 'team' && message.sender_name !== undefined && message.sender_name !== null && message.sender_name !== '') {
      return message.sender_name;
    }
    return t('feedback.thread.team');
  };
  return (
    <ol aria-label={t('feedback.thread.label')} className="flex flex-col gap-2" data-report-thread="">
      {[...messages].sort((a, b) => a.created_at.localeCompare(b.created_at)).map((message) => (
        <li
          key={message.message_id}
          className={`rounded-control flex flex-col gap-1 px-3 py-2 ${message.side === 'author' ? 'bg-pill' : 'border-hairline border'}`}
        >
          <p className="text-caption text-ink-primary flex flex-wrap items-baseline gap-x-2">
            <span className="font-semibold" translate="no">{writer(message)}</span>
            <time dateTime={message.created_at} className="font-numeric">{at(message.created_at)}</time>
          </p>
          <p className="text-body text-ink-primary whitespace-pre-wrap break-words" translate="no">{message.body}</p>
        </li>
      ))}
    </ol>
  );
}

export function ReportHistory({ transitions, viewer }: {
  readonly transitions: readonly ReportTransition[];
  readonly viewer: ThreadViewer;
}): React.JSX.Element {
  const { t } = useTranslation();
  const at = useAt();
  if (transitions.length === 0) {
    return <p className="text-body text-ink-secondary">{t('feedback.history.empty')}</p>;
  }
  // The author's history carries no actor: the status and the instant are what it says.
  const who = (move: ReportTransition): string | null => {
    const side = move.actor_side;
    if (side === undefined || side === '') return null;
    if (side === 'author' && viewer === 'author') return t('feedback.thread.you');
    const sideWords = Object.hasOwn(ACTOR_SIDE_LABEL_KEY, side) ? t(ACTOR_SIDE_LABEL_KEY[side as keyof typeof ACTOR_SIDE_LABEL_KEY]) : side;
    if (viewer === 'team' && move.actor_name !== undefined && move.actor_name !== null && move.actor_name !== '') {
      return t('feedback.history.byNamed', { name: move.actor_name, side: sideWords });
    }
    return sideWords;
  };
  return (
    <ol aria-label={t('feedback.history.label')} className="flex flex-col gap-1" data-report-history="">
      {[...transitions].sort((a, b) => a.at.localeCompare(b.at)).map((move, index) => (
        <li key={`${move.at}-${index}`} className="border-hairline grid gap-x-3 border-b py-1 sm:grid-cols-[minmax(9rem,auto)_1fr]">
          <time dateTime={move.at} className="text-caption text-ink-secondary font-numeric">{at(move.at)}</time>
          <span className="text-body text-ink-primary flex flex-wrap items-center gap-x-2">
            <ReportStatus status={move.to_status} />
            {move.to_kind !== undefined && move.to_kind !== null && move.to_kind !== move.from_kind && (
              <span className="text-caption">{t('feedback.history.relabelled', { kind: kindWords(t, move.to_kind) })}</span>
            )}
            {who(move) !== null && <span className="text-caption text-ink-secondary" translate="no">{who(move)}</span>}
          </span>
        </li>
      ))}
    </ol>
  );
}
